import { z } from 'zod'
import { basisPointsSchema, minorNonNegativeSchema } from './common.js'

/**
 * Payment methods available when marking a jobcard as paid.
 * Each method has configurable processing fees.
 */
export const PAYMENT_METHODS = [
  'cash',
  'bank_transfer',
  'card_machine',
  'link_tabby',
  'link_tamara',
  'link_card',
  'other',
] as const
export type PaymentMethod = (typeof PAYMENT_METHODS)[number]

export const paymentMethodSchema = z.enum(PAYMENT_METHODS)

/**
 * Fee rate configuration for a single payment method.
 * Fee = roundHalfUp(amount * rateBp / 10000) + fixedMinor
 */
export const paymentFeeRateSchema = z.object({
  /** Basis points (0.01%): 250 = 2.5%. Defaults to 0. */
  rateBp: basisPointsSchema.default(0),
  /** Fixed fee in minor units, added after the percentage. Defaults to 0. */
  fixedMinor: minorNonNegativeSchema.default(0),
})
export type PaymentFeeRate = z.infer<typeof paymentFeeRateSchema>

/**
 * Payment fee rates, keyed by payment method.
 * All methods are included, with defaults where not set.
 */
export const paymentFeeRatesSchema = z.record(
  paymentMethodSchema,
  paymentFeeRateSchema,
).default({
  cash: { rateBp: 0, fixedMinor: 0 },
  bank_transfer: { rateBp: 0, fixedMinor: 0 },
  card_machine: { rateBp: 250, fixedMinor: 0 },
  link_tabby: { rateBp: 0, fixedMinor: 0 },
  link_tamara: { rateBp: 0, fixedMinor: 0 },
  link_card: { rateBp: 0, fixedMinor: 0 },
  other: { rateBp: 0, fixedMinor: 0 },
})
export type PaymentFeeRates = z.infer<typeof paymentFeeRatesSchema>
